import { useState, useEffect, useCallback } from 'react';
import Sidebar from './Sidebar';
import {
  getNextReview,
  getReviewAudioUrl,
  addReviewComment,
  deleteReviewComment,
} from '../services/api';

const MAX_COMMENT_CHARS = 2000; // keep in sync with the backend's MAX_COMMENT_CHARS

const errorMessage = (e) => e.response?.data?.detail || e.message;

const ACCENT_BTN = {
  background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8,
  padding: '10px 20px', fontWeight: 700, fontSize: 14,
};

export default function ConversationReview({ user, onPageChange }) {
  const [review, setReview]                 = useState(null); // the one conversation the backend chose
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [commentText, setCommentText]       = useState('');
  const [stagedComments, setStagedComments] = useState([]); // added but not saved until Submit
  const [submitting, setSubmitting]         = useState(false);

  const loadNext = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReview(await getNextReview());
    } catch (e) {
      setError(errorMessage(e));
    }
    setStagedComments([]);
    setCommentText('');
    setLoading(false);
  }, []);

  useEffect(() => { loadNext(); }, [loadNext]);

  const transcript = review?.transcript || [];
  const savedComments = review?.comments || [];
  const typedComment = commentText.trim();
  const pendingCount = stagedComments.length + (typedComment ? 1 : 0);

  const stageComment = () => {
    if (!typedComment) return;
    setStagedComments((s) => [...s, typedComment]);
    setCommentText('');
  };

  const unstageComment = (index) => setStagedComments((s) => s.filter((_, i) => i !== index));

  // Saves staged comments (plus any text still typed in the box), then asks the backend for the next conversation.
  const submit = async () => {
    const toSave = typedComment ? [...stagedComments, typedComment] : [...stagedComments];
    if (!toSave.length) return;
    setSubmitting(true);
    setError(null);
    const saved = [];
    try {
      for (const text of toSave) {
        saved.push(await addReviewComment(review.id, { text }));
      }
    } catch (e) {
      // Keep only the comments that were not saved so a retry doesn't duplicate.
      setStagedComments(toSave.slice(saved.length));
      setCommentText('');
      setReview((r) => ({ ...r, comments: [...r.comments, ...saved] }));
      setError(errorMessage(e));
      setSubmitting(false);
      return;
    }
    // Show what was saved, so it stays visible if fetching the next conversation fails.
    setReview((r) => ({ ...r, comments: [...r.comments, ...saved] }));
    await loadNext();
    setSubmitting(false);
  };

  const deleteSavedComment = async (commentId) => {
    setError(null);
    try {
      await deleteReviewComment(review.id, commentId);
      setReview((r) => ({ ...r, comments: r.comments.filter((c) => c.id !== commentId) }));
    } catch (e) { setError(errorMessage(e)); }
  };

  return (
    <div className="workspace">
      <Sidebar
        user={user}
        currentPage="conversation-review"
        onPageChange={onPageChange}
        title={user?.name || user?.email || 'Reviewer'}
        subtitle="Conversation Review"
      />
      <main className="annotator" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div className="annotator-header" style={{ flexShrink: 0 }}>
          <h1 style={{ margin: 0 }}>Conversation Review</h1>
        </div>

        {error && <div style={{ color: '#ef4444', padding: '8px 16px' }}>{error}</div>}
        {loading && <div style={{ padding: 16 }}>Loading…</div>}

        {!loading && !review && !error && (
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 12 }}>
            <div>You have reviewed every conversation. Nothing left to review.</div>
            <button onClick={loadNext} style={{ ...ACCENT_BTN, cursor: 'pointer' }}>Check again</button>
          </div>
        )}

        {review && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>🔊 {review.name}</div>
              <audio
                key={review.id}
                src={getReviewAudioUrl(review.id)}
                controls
                preload="metadata"
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
              {/* Transcript */}
              <div style={{ flex: 2, overflowY: 'auto', padding: 16, borderRight: '1px solid var(--border)' }}>
                {transcript.map((line, i) => (
                  <div key={i} style={{ padding: '6px 10px', marginBottom: 4 }}>
                    <strong style={{ color: line.speaker === 'Bot' ? '#a855f7' : '#22c55e', marginRight: 6 }}>{line.speaker}</strong>
                    {line.text}
                  </div>
                ))}
              </div>

              {/* Comments */}
              <div style={{ flex: 1, minWidth: 280, display: 'flex', flexDirection: 'column', padding: 16, gap: 8, overflow: 'hidden' }}>
                <textarea
                  rows={4}
                  value={commentText}
                  maxLength={MAX_COMMENT_CHARS}
                  placeholder="Add a comment…"
                  onChange={(e) => setCommentText(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    disabled={!typedComment || submitting}
                    onClick={stageComment}
                    style={{
                      flex: 1, background: 'transparent', color: 'var(--text)', border: '1px solid var(--border)',
                      borderRadius: 8, padding: '10px 12px', fontWeight: 600, fontSize: 14,
                      cursor: !typedComment || submitting ? 'not-allowed' : 'pointer',
                      opacity: !typedComment || submitting ? 0.55 : 1,
                    }}
                  >
                    Add comment
                  </button>
                  <button
                    disabled={!pendingCount || submitting}
                    onClick={submit}
                    style={{
                      ...ACCENT_BTN, flex: 1,
                      cursor: !pendingCount || submitting ? 'not-allowed' : 'pointer',
                      opacity: !pendingCount || submitting ? 0.55 : 1,
                    }}
                  >
                    {submitting ? 'Saving…' : `Submit${pendingCount ? ` (${pendingCount})` : ''}`}
                  </button>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', marginTop: 8 }}>
                  {stagedComments.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted,#888)', marginBottom: 4 }}>
                        Not saved yet. Click Submit to save and go to the next conversation.
                      </div>
                      {stagedComments.map((text, i) => (
                        <div key={i} style={{ border: '1px dashed var(--border)', borderRadius: 6, padding: '6px 8px', marginBottom: 4, fontSize: 13, display: 'flex', gap: 8 }}>
                          <div style={{ flex: 1, whiteSpace: 'pre-wrap' }}>{text}</div>
                          <button onClick={() => unstageComment(i)} disabled={submitting} aria-label="Remove staged comment">✕</button>
                        </div>
                      ))}
                    </div>
                  )}

                  {!savedComments.length && !stagedComments.length && (
                    <div style={{ color: 'var(--text-muted,#888)', fontSize: 13 }}>No comments yet.</div>
                  )}
                  {savedComments.map((c) => (
                    <div key={c.id} style={{ borderBottom: '1px solid var(--border)', padding: '8px 0', fontSize: 13 }}>
                      <div style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-muted,#888)' }}>
                        <span>{c.author}</span>
                        {c.author === user?.email && (
                          <button onClick={() => deleteSavedComment(c.id)} style={{ marginLeft: 'auto' }}>Delete</button>
                        )}
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{c.text}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
